import { useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "@/components/ui/carousel";
import Autoplay from "embla-carousel-autoplay";

export default function ServiceGallerySlider({ images, serviceName }) {
  const [selectedIndex, setSelectedIndex] = useState(null);
  // Single autoplay plugin instance (standard shadcn/embla pattern)
  const autoplay = useRef(
    Autoplay({ delay: 3000, stopOnInteraction: false, stopOnMouseEnter: true })
  );

  const handleImageClick = (index) => {
    setSelectedIndex(index);
    try { autoplay.current?.stop?.(); } catch (e) { /* noop */ }
  };

  const handleModalClose = () => {
    setSelectedIndex(null);
    try { autoplay.current?.play?.(); } catch (e) { /* noop */ }
  };

  const handlePrevImage = useCallback(() => {
    setSelectedIndex((i) => (i > 0 ? i - 1 : images.length - 1));
  }, [images.length]);

  const handleNextImage = useCallback(() => {
    setSelectedIndex((i) => (i < images.length - 1 ? i + 1 : 0));
  }, [images.length]);

  // Keyboard navigation for modal
  useEffect(() => {
    if (selectedIndex === null) return;
    const handleKeyDown = (e) => {
      if (e.key === "ArrowLeft") handlePrevImage();
      if (e.key === "ArrowRight") handleNextImage();
      if (e.key === "Escape") handleModalClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedIndex, handlePrevImage, handleNextImage]);

  return (
    <>
      {/* Main Carousel Slider */}
      <Carousel
        opts={{ align: "start", loop: true, slidesToScroll: 1 }}
        plugins={[autoplay.current]}
        className="w-full"
      >
        <CarouselContent className="-ml-4">
          {images.map((src, i) => (
            <CarouselItem
              key={i}
              className="pl-4 basis-full sm:basis-1/2 lg:basis-1/4 xl:basis-1/5"
            >
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="overflow-hidden rounded-2xl shadow-sm aspect-[4/3] group cursor-pointer"
                onClick={() => handleImageClick(i)}
              >
                <img
                  src={src}
                  alt={`${serviceName} gallery ${i + 1}`}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </motion.div>
            </CarouselItem>
          ))}
        </CarouselContent>

        {/* Navigation Buttons */}
        <div className="hidden lg:block absolute -left-14 top-1/2 -translate-y-1/2">
          <CarouselPrevious variant="outline" className="border-brand-cream bg-white hover:bg-brand-cream" />
        </div>
        <div className="hidden lg:block absolute -right-14 top-1/2 -translate-y-1/2">
          <CarouselNext variant="outline" className="border-brand-cream bg-white hover:bg-brand-cream" />
        </div>
      </Carousel>

      {/* Fullscreen Image Modal */}
      <AnimatePresence>
        {selectedIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleModalClose}
            className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          >
            {/* Modal Content */}
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full h-full flex items-center justify-center px-4"
            >
              {/* Main Image */}
              <div className="relative w-full max-w-5xl aspect-[4/3]">
                <motion.img
                  key={selectedIndex}
                  src={images[selectedIndex]}
                  alt={`${serviceName} gallery ${selectedIndex + 1}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="w-full h-full object-cover rounded-lg"
                />
              </div>

              {/* Left Arrow */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={handlePrevImage}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors text-white"
                aria-label="Previous image"
              >
                <ChevronLeft size={32} />
              </motion.button>

              {/* Right Arrow */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleNextImage}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors text-white"
                aria-label="Next image"
              >
                <ChevronRight size={32} />
              </motion.button>

              {/* Close Button */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleModalClose}
                className="absolute top-4 right-4 z-20 p-2 rounded-full bg-white/20 hover:bg-white/30 transition-colors text-white"
                aria-label="Close gallery"
              >
                <X size={28} />
              </motion.button>

              {/* Image Counter */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-4 py-2 rounded-full text-sm font-medium">
                {selectedIndex + 1} / {images.length}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
